<script setup lang="ts">
import { ref, watch, computed, onMounted, onUnmounted } from "vue";
import { useTranslation } from "i18next-vue";
import { useComponentErrorCapture } from "@/mixins/error";
import { useTiming } from "@/mixins/timing";
import { isTouchEnabled } from "@/mixins/device";
import { useModal } from "@/mixins/modal";
import { appStateStore } from "@/stores/appState";
import { hideMessage } from "@/stores/messages";

const { t } = useTranslation();
const { sleep } = useTiming();
const { registerOpenModal, unregisterModal } = useModal();
useComponentErrorCapture();

const emit = defineEmits(["close", "submit"]);

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
  bodyCover: {
    type: Boolean,
    default: true,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  focusableInput: {
    type: Boolean,
    default: true,
  },
  buttonClose: {
    type: Boolean,
    default: true,
  },
  buttonBack: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "100%",
  },
  height: {
    type: String,
    default: "auto",
  },
  top: {
    type: String,
    default: "",
  },
  left: {
    type: String,
    default: "",
  },
  saving: {
    type: Boolean,
    default: false,
  },
  slide: {
    type: Boolean,
    default: false,
  },
});

const modal = ref<HTMLDivElement | null>(null);
const appState = appStateStore();

// For animation
const showModal = ref(false);

defineExpose({
  triggerClose
});

watch(props, async () => {
  if (props.show) {
    registerOpenModal(props.name, !props.closeable);
    await sleep(30);
    showModal.value = true;
    
    if (!isTouchEnabled()) {
      await sleep(100);
      const focusElement = modal.value as HTMLDivElement;
      let input;
      if (focusElement && props.focusableInput) {
        input = focusElement.querySelector("input, textarea") as HTMLDivElement;
        if (input?.closest("[data-nofocus]")) {
          return;
        }
      }
      (input ? input : focusElement)?.focus();
    }
  } else {
    await sleep(100);
    showModal.value = false;
    unregisterModal(props.name, !props.closeable);
  }
});

appState.$subscribe((mutation, state) => {
  if (props.show && !state.modalPool.includes(props.name) && props.closeable) {
    triggerClose();
  }

  if (state.modalPool.length > 0) {
    document.body.classList.add('overflow-hidden');
  } else {
    document.body.classList.remove('overflow-hidden');
  }
});

const containerStyle = computed(() => {
  let styleList = "";
  styleList += `width:${props.width};height:${props.height};`;
  styleList += props.height != 'auto' ? `min-height:${props.height};` : '';

  if (props.top.length > 0 && props.left.length > 0) {
    styleList += `position:fixed;top:${props.top};left:${props.left};`;
  }

  return styleList;
});

const containerTransition = computed(() => {
  if (props.slide) {
    return 'slide-from-bottom';
  }
    
  return 'opacity-fast';
});


async function triggerClose() {
  if (!props.closeable) {
    return;
  }

  showModal.value = false;
  await sleep(500);
  hideMessage();
  emit("close");
}

function triggerSubmit() {
  emit("submit");
}

onMounted(() => {
  showModal.value = props.show;
});

onUnmounted(() => {
  unregisterModal(props.name, !props.closeable);
});
</script>
<template>
  <div
    v-if="show || showModal"
    class="modal-secondary"
    :class="{'modal-secondary--saving': saving}"
    @keyup.enter.stop="triggerSubmit"
    ref="modal"
  >
    <Transition name="opacity">
      <div v-show="show && showModal" class="modal-secondary__background"></div>
    </Transition>
    <Transition :name="containerTransition">
      <div v-show="show && showModal" class="modal-secondary__container" :style="containerStyle">
        <div v-if="!!$slots.header" class="modal-secondary__header">
          <slot name="header" />
          <Transition name="opacity-fast">
            <button
              v-show="buttonClose && !saving"
              @click="triggerClose"
              class="button button--secondary button--icon button--icon--small button-close">
              <i class="fa-regular fa-close"></i>
            </button>
          </Transition>
          <Transition name="opacity-fast">
            <div v-show="saving" class="modal-secondary__loader">
              <img src="/images/loading-indicator.png" alt="Loading" class="img-loading">
            </div>
          </Transition>
        </div>

        <div v-if="bodyCover" class="modal-secondary__body">
          <slot />
        </div>
        <slot v-if="!bodyCover" />

        <div v-if="!!$slots.footer" class="modal-secondary__footer">
          <slot name="footer" />
        </div>
      </div>
    </Transition>
  </div>
</template>
